import '@/assets/scss/main.scss';

import { createHooks } from 'hookable';
import { getContext } from 'unctx';
import plugins from 'virtual:vite-auto-plugins';
import { createApp, effectScope } from 'vue';

import App from './App.vue';
import isFunction from './utils/isFunction';
import isNullOrUndefined from './utils/isNullOrUndefined';
import isObject from './utils/isObject';

import type { Hookable } from 'hookable';
import type { App as VueApp } from 'vue';
import type { NavigationGuard, RouteLocationNormalized } from 'vue-router';

export interface Hooks {
  'app:created': (app: VueApp<Element>) => MaybePromise<void>;
}

export interface Context {
  app: VueApp<Element>;
  provide(name: string, value: any): void;

  hooks: Hookable<Hooks>;

  [key: string]: any;
}

type MaybePromise<T> = T | Promise<T>;

export interface Plugin<
  Injections extends Record<string, any> = Record<string, any>
> {
  (context: Context): MaybePromise<void> | MaybePromise<Injections>;
}

export function defineAppPlugin(plugin: Plugin) {
  return plugin;
}

export async function applyPlugin(context: Context, plugin: Plugin) {
  if (!isFunction(plugin)) return;
  const result = await context.app.runWithContext(() => plugin(context));
  const { provide } = result || {};

  if (provide && isObject(provide)) {
    for (const key in provide) {
      context.provide(key, provide[key]);
    }
  }
}

export async function applyPlugins(context: Context, plugins: Plugin[]) {
  for (const plugin of plugins) {
    await applyPlugin(context, plugin);
  }
}

export interface RouteMiddleware {
  (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized
  ): ReturnType<NavigationGuard>;
}

export function defineAppRouteMiddleware(middleware: RouteMiddleware) {
  return middleware;
}

export function useApp() {
  const appInstance = getContext<Context>('app')?.tryUse();
  if (!appInstance) throw new Error('[app] App not initialized');
  return appInstance;
}

async function initApp() {
  const app = createApp(App);

  // NOTE: Vue 3.4 will support top-level effect scope
  const scope = effectScope(true);

  const runWithContext = app.runWithContext;
  const unmount = app.unmount;

  app.runWithContext = fn => runWithContext(() => scope.run(fn)!);
  app.unmount = () => {
    unmount();
    scope.stop();
  };

  const context = {
    app,
    hooks: createHooks<Hooks>(),
  } as any as Context;

  const globalContext = getContext<Context>('app');
  globalContext.set(context);

  context.provide = (name, value) => {
    const $name = '$' + name;

    defineGetter(context, $name, value);
    defineGetter(app.config.globalProperties, $name, value);
  };

  await applyPlugins(context, plugins);
  await context.hooks.callHook('app:created', app);

  app.mount('#app');
}

function defineGetter<K extends string | number | symbol, V>(
  obj: Record<K, V>,
  key: K,
  val: V
) {
  if (key in obj && !isNullOrUndefined(obj[key])) return;
  Object.defineProperty(obj, key, { get: () => val });
}

initApp();
